/**
 * Premium Shopping Cart Functionality
 * Modern cart interactions with smooth animations
 */

class PremiumCart {
  constructor() {
    this.cart = null;
    this.drawer = document.querySelector('cart-drawer');
    this.init();
  }

  init() {
    this.fetchCart();
    this.bindEvents();
    this.initAnimations();
    this.initUpsells();
    this.initPromoCode();
  }

  /**
   * Fetch current cart data
   */
  async fetchCart() {
    try {
      const response = await fetch('/cart.js');
      this.cart = await response.json();
      this.updateCartUI();
    } catch (error) {
      console.error('Error fetching cart:', error);
    }
  }

  /**
   * Bind cart events
   */
  bindEvents() {
    // Add to cart buttons
    document.addEventListener('click', async (e) => {
      // Add to cart
      if (e.target.closest('[data-add-to-cart]')) {
        e.preventDefault();
        const button = e.target.closest('[data-add-to-cart]');
        const productId = button.dataset.productId;
        const variantId = button.dataset.variantId || await this.getDefaultVariant(productId);
        
        await this.addToCart(variantId, 1);
        this.showAddedAnimation(button);
      }

      // Quick add to cart
      if (e.target.closest('.btn-add-to-cart')) {
        e.preventDefault();
        const button = e.target.closest('.btn-add-to-cart');
        const productId = button.dataset.productId;
        
        await this.quickAddToCart(productId);
        this.showAddedAnimation(button);
      }

      // Remove from cart
      if (e.target.closest('[data-remove-item]')) {
        const key = e.target.closest('[data-remove-item]').dataset.removeItem;
        await this.removeFromCart(key);
      }

      // Open cart drawer
      if (e.target.closest('[data-open-cart]')) {
        e.preventDefault();
        this.openDrawer();
      }

      // Close cart drawer
      if (e.target.closest('[data-close-cart]')) {
        e.preventDefault();
        this.closeDrawer();
      }
    });

    // Quantity adjustments
    document.addEventListener('click', async (e) => {
      if (e.target.closest('[data-qty-adjust]')) {
        const button = e.target.closest('[data-qty-adjust]');
        const key = button.dataset.key;
        const adjustment = parseInt(button.dataset.qtyAdjust);
        
        const input = document.querySelector(`.qty-input[data-key="${key}"]`);
        const currentQty = parseInt(input.value);
        const newQty = Math.max(1, currentQty + adjustment);
        
        await this.updateQuantity(key, newQty);
      }
    });

    // Quantity input change
    document.addEventListener('change', async (e) => {
      if (e.target.classList.contains('qty-input')) {
        const key = e.target.dataset.key;
        const quantity = parseInt(e.target.value);
        
        if (quantity > 0) {
          await this.updateQuantity(key, quantity);
        }
      }
    });

    // ESC key to close drawer
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.drawer?.hasAttribute('open')) {
        this.closeDrawer();
      }
    });
  }

  /**
   * Add item to cart
   */
  async addToCart(variantId, quantity = 1) {
    try {
      const formData = {
        items: [{
          id: variantId,
          quantity: quantity
        }]
      };

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        await this.fetchCart();
        this.openDrawer();
        this.showNotification('Added to cart!', 'success');
        this.updateCartCount();
        this.triggerConfetti();
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      this.showNotification('Error adding to cart', 'error');
    }
  }

  /**
   * Quick add to cart (for product cards)
   */
  async quickAddToCart(productId) {
    try {
      // First, get product data
      const response = await fetch(`/products/${productId}.js`);
      const product = await response.json();
      
      // Get first available variant
      const variant = product.variants.find(v => v.available) || product.variants[0];
      
      if (variant) {
        await this.addToCart(variant.id, 1);
      }
    } catch (error) {
      console.error('Error with quick add:', error);
    }
  }

  /**
   * Update item quantity
   */
  async updateQuantity(key, quantity) {
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: key,
          quantity: quantity
        })
      });

      if (response.ok) {
        await this.fetchCart();
        this.animateCartUpdate();
      }
    } catch (error) {
      console.error('Error updating quantity:', error);
    }
  }

  /**
   * Remove item from cart
   */
  async removeFromCart(key) {
    try {
      // Animate removal
      const item = document.querySelector(`[data-key="${key}"]`);
      if (item) {
        item.style.animation = 'slideOut 0.3s ease';
        await new Promise(resolve => setTimeout(resolve, 300));
      }

      await this.updateQuantity(key, 0);
      this.showNotification('Item removed', 'info');
    } catch (error) {
      console.error('Error removing item:', error);
    }
  }

  /**
   * Update cart UI
   */
  updateCartUI() {
    if (!this.cart) return;

    // Update cart count
    this.updateCartCount();

    // Update cart items
    const itemsContainer = document.querySelector('[data-cart-items]');
    if (!itemsContainer) return;

    if (this.cart.item_count === 0) {
      itemsContainer.innerHTML = this.getEmptyCartHTML();
    } else {
      itemsContainer.innerHTML = this.cart.items.map(item => this.getCartItemHTML(item)).join('');
    }

    // Update summary
    this.updateCartSummary();

    // Update free shipping progress
    this.updateShippingProgress();
  }

  /**
   * Get cart item HTML
   */
  getCartItemHTML(item) {
    return `
      <div class="cart-item" data-cart-item data-key="${item.key}">
        <div class="cart-item__image">
          ${item.image ? `<img src="${item.image}" alt="${item.title}" loading="lazy">` : ''}
          ${item.properties?.event_date ? `
            <div class="event-date-overlay">
              ${new Date(item.properties.event_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </div>
          ` : ''}
        </div>
        
        <div class="cart-item__details">
          <h3 class="cart-item__title">
            <a href="${item.url}">${item.product_title}</a>
          </h3>
          
          ${item.properties?.venue ? `
            <p class="cart-item__venue">📍 ${item.properties.venue}</p>
          ` : ''}
          
          ${item.variant_title && item.variant_title !== 'Default Title' ? `
            <div class="cart-item__variants">
              <span class="variant-tag">${item.variant_title}</span>
            </div>
          ` : ''}
          
          <div class="cart-item__price-quantity">
            <div class="quantity-selector">
              <button class="qty-btn qty-minus" data-qty-adjust="-1" data-key="${item.key}">-</button>
              <input type="number" class="qty-input" value="${item.quantity}" min="1" data-key="${item.key}">
              <button class="qty-btn qty-plus" data-qty-adjust="1" data-key="${item.key}">+</button>
            </div>
            
            <div class="cart-item__price">
              ${item.original_price !== item.final_price ? `
                <span class="price-compare">${this.formatMoney(item.original_price)}</span>
              ` : ''}
              <span class="price-final">${this.formatMoney(item.final_price)}</span>
            </div>
          </div>
          
          <button class="cart-item__remove" data-remove-item="${item.key}">Remove</button>
        </div>
      </div>
    `;
  }

  /**
   * Get empty cart HTML
   */
  getEmptyCartHTML() {
    return `
      <div class="cart-empty">
        <div class="empty-cart-icon">
          <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1">
            <circle cx="9" cy="21" r="1"/>
            <circle cx="20" cy="21" r="1"/>
            <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
          </svg>
        </div>
        <h3>Your cart is empty</h3>
        <p>Time to grab some amazing tickets!</p>
        <a href="/collections/all" class="btn-continue-shopping">Browse Events</a>
      </div>
    `;
  }

  /**
   * Update cart summary
   */
  updateCartSummary() {
    const summaryElements = {
      subtotal: document.querySelector('[data-cart-subtotal]'),
      discount: document.querySelector('[data-cart-discount]'),
      total: document.querySelector('[data-cart-total]')
    };

    if (summaryElements.subtotal) {
      summaryElements.subtotal.textContent = this.formatMoney(this.cart.items_subtotal_price);
    }

    if (summaryElements.discount && this.cart.total_discount > 0) {
      summaryElements.discount.textContent = `-${this.formatMoney(this.cart.total_discount)}`;
      summaryElements.discount.closest('.summary-row').style.display = 'flex';
    }

    if (summaryElements.total) {
      summaryElements.total.textContent = this.formatMoney(this.cart.total_price);
    }
  }

  /**
   * Update cart count badges
   */
  updateCartCount() {
    // Update the cart-count custom element (primary header cart icon)
    const cartCountElement = document.querySelector('cart-count');
    if (cartCountElement) {
      // Update the visible count
      const countSpan = cartCountElement.querySelector('span[aria-hidden="true"]');
      if (countSpan) {
        countSpan.textContent = this.cart.item_count;
      }
      
      // Update screen reader text
      const srSpan = cartCountElement.querySelector('.sr-only');
      if (srSpan) {
        srSpan.textContent = srSpan.textContent.replace(/\d+/, this.cart.item_count);
      }
      
      // Show/hide the count bubble based on item count
      if (this.cart.item_count > 0) {
        cartCountElement.classList.remove('opacity-0');
      } else {
        cartCountElement.classList.add('opacity-0');
      }
      
      // Add pulse animation for updates
      cartCountElement.style.animation = 'none';
      setTimeout(() => {
        cartCountElement.style.animation = 'pulse 0.5s ease';
      }, 10);
    }
    
    // Also update any elements with data-cart-count attribute (for compatibility)
    const countElements = document.querySelectorAll('[data-cart-count]');
    countElements.forEach(el => {
      el.textContent = this.cart.item_count;
      
      // Add pulse animation for updates
      el.style.animation = 'none';
      setTimeout(() => {
        el.style.animation = 'pulse 0.5s ease';
      }, 10);
    });
  }

  /**
   * Update free shipping progress
   */
  updateShippingProgress() {
    const threshold = 10000; // $100 in cents
    const progress = Math.min(100, (this.cart.total_price / threshold) * 100);
    const remaining = Math.max(0, threshold - this.cart.total_price);

    const progressBar = document.querySelector('.progress-fill');
    const progressText = document.querySelector('.progress-text');

    if (progressBar) {
      progressBar.style.width = `${progress}%`;
    }

    if (progressText) {
      if (progress >= 100) {
        progressText.innerHTML = '<strong>🎉 Congrats! You\'ve unlocked FREE SHIPPING!</strong>';
        progressText.classList.add('success');
      } else {
        progressText.innerHTML = `
          <span class="highlight">${this.formatMoney(remaining)}</span> away from <strong>FREE SHIPPING!</strong> 🚀
        `;
        progressText.classList.remove('success');
      }
    }
  }

  /**
   * Open cart drawer
   */
  openDrawer() {
    if (this.drawer) {
      this.drawer.setAttribute('open', '');
      document.body.style.overflow = 'hidden';
      this.fetchCart(); // Refresh cart data
    }
  }

  /**
   * Close cart drawer
   */
  closeDrawer() {
    if (this.drawer) {
      this.drawer.removeAttribute('open');
      document.body.style.overflow = '';
    }
  }

  /**
   * Show notification
   */
  showNotification(message, type = 'success') {
    const notification = document.createElement('div');
    notification.className = `cart-notification cart-notification--${type}`;
    notification.innerHTML = `
      <div class="notification-content">
        <span class="notification-icon">${type === 'success' ? '✓' : '!'}</span>
        <span class="notification-message">${message}</span>
      </div>
    `;

    document.body.appendChild(notification);

    setTimeout(() => notification.classList.add('show'), 100);
    setTimeout(() => {
      notification.classList.remove('show');
      setTimeout(() => notification.remove(), 300);
    }, 3000);
  }

  /**
   * Show added to cart animation
   */
  showAddedAnimation(button) {
    const originalText = button.innerHTML;
    button.innerHTML = '✓ Added!';
    button.classList.add('added');

    setTimeout(() => {
      button.innerHTML = originalText;
      button.classList.remove('added');
    }, 2000);
  }

  /**
   * Animate cart update
   */
  animateCartUpdate() {
    const cartItems = document.querySelectorAll('.cart-item');
    cartItems.forEach((item, index) => {
      item.style.animation = 'none';
      setTimeout(() => {
        item.style.animation = `slideIn 0.3s ease ${index * 0.05}s`;
      }, 10);
    });
  }

  /**
   * Initialize animations
   */
  initAnimations() {
    // Add CSS for animations
    const style = document.createElement('style');
    style.innerHTML = `
      @keyframes slideOut {
        to {
          opacity: 0;
          transform: translateX(100%);
        }
      }
      
      @keyframes pulse {
        0%, 100% { transform: scale(1); }
        50% { transform: scale(1.1); }
      }
      
      .cart-notification {
        position: fixed;
        top: 20px;
        right: 20px;
        background: white;
        border-radius: 12px;
        padding: 1rem 1.5rem;
        box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
        z-index: 10000;
        transform: translateX(400px);
        transition: transform 0.3s ease;
      }
      
      .cart-notification.show {
        transform: translateX(0);
      }
      
      .cart-notification--success {
        border-left: 4px solid #10b981;
      }
      
      .cart-notification--error {
        border-left: 4px solid #ef4444;
      }
      
      .notification-content {
        display: flex;
        align-items: center;
        gap: 0.75rem;
      }
      
      .notification-icon {
        width: 24px;
        height: 24px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: bold;
      }
      
      .cart-notification--success .notification-icon {
        background: #dcfce7;
        color: #10b981;
      }
      
      .cart-notification--error .notification-icon {
        background: #fee2e2;
        color: #ef4444;
      }
      
      .btn-add-to-cart.added {
        background: #10b981 !important;
        animation: pulse 0.5s ease;
      }
    `;
    document.head.appendChild(style);
  }

  /**
   * Initialize upsell products
   */
  initUpsells() {
    // Fetch and display upsell products based on cart contents
    // This would typically fetch related products from your backend
  }

  /**
   * Initialize promo code functionality
   */
  initPromoCode() {
    const promoButton = document.querySelector('.btn-apply-promo');
    const promoInput = document.querySelector('.promo-input');

    if (promoButton && promoInput) {
      promoButton.addEventListener('click', async () => {
        const code = promoInput.value.trim();
        if (code) {
          await this.applyPromoCode(code);
        }
      });

      promoInput.addEventListener('keypress', async (e) => {
        if (e.key === 'Enter') {
          const code = promoInput.value.trim();
          if (code) {
            await this.applyPromoCode(code);
          }
        }
      });
    }
  }

  /**
   * Apply promo code
   */
  async applyPromoCode(code) {
    try {
      // This would typically make an API call to apply the discount
      // For now, we'll just show a notification
      this.showNotification(`Promo code "${code}" applied!`, 'success');
      
      // Clear input
      const promoInput = document.querySelector('.promo-input');
      if (promoInput) {
        promoInput.value = '';
      }

      // Refresh cart
      await this.fetchCart();
    } catch (error) {
      console.error('Error applying promo code:', error);
      this.showNotification('Invalid promo code', 'error');
    }
  }

  /**
   * Trigger confetti animation
   */
  triggerConfetti() {
    // Add confetti library if not already loaded
    if (typeof confetti === 'undefined') {
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/canvas-confetti@1.6.0/dist/confetti.browser.min.js';
      script.onload = () => this.fireConfetti();
      document.head.appendChild(script);
    } else {
      this.fireConfetti();
    }
  }

  fireConfetti() {
    if (typeof confetti !== 'undefined') {
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 }
      });
    }
  }

  /**
   * Format money
   */
  formatMoney(cents) {
    return `$${(cents / 100).toFixed(2)}`;
  }

  /**
   * Get default variant for product
   */
  async getDefaultVariant(productId) {
    try {
      const response = await fetch(`/products/${productId}.js`);
      const product = await response.json();
      return product.variants[0].id;
    } catch (error) {
      console.error('Error getting default variant:', error);
      return null;
    }
  }
}

// Initialize cart when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.premiumCart = new PremiumCart();
  });
} else {
  window.premiumCart = new PremiumCart();
}

// Define custom element for cart drawer
class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', (e) => {
      if (e.target === this) {
        this.close();
      }
    });
  }

  open() {
    this.setAttribute('open', '');
    document.body.style.overflow = 'hidden';
  }

  close() {
    this.removeAttribute('open');
    document.body.style.overflow = '';
  }
}

// Check if cart-drawer is already defined to avoid duplicate definition error
if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}